import React, { useState } from 'react';
import { resolveColor } from '../utils/colorResolver';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ProductoCatalogoPublico } from '../types';
import { CatalogoImageCarousel } from './CatalogoImageCarousel';
import { CATALOGO_TARGET_CARD_MAX_WIDTH_PX } from '../utils/layoutRules';

interface CatalogoCardProps {
  producto: ProductoCatalogoPublico;
  onClick: (producto: ProductoCatalogoPublico) => void;
  onAgregarClick?: (producto: ProductoCatalogoPublico, imagenUrl: string | null) => void;
}

const formatPrecio = (precio: number | null): string => {
  if (precio === null) return '—';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(precio);
};

export function CatalogoCard({ producto, onClick, onAgregarClick }: CatalogoCardProps) {
  const coloresDisponibles = producto.coloresDisponibles.slice(0, 5);
  const stockBajo = producto.stockTotal > 0 && producto.stockTotal <= 10;
  const [activeImageUrl, setActiveImageUrl] = useState<string | null>(null);

  return (
    <Card
      role="article"
      aria-label={`Producto del catálogo: ${producto.nombre}`}
      data-testid="catalogo-card"
      sx={{
        backgroundColor: '#ffffff',
        height: '100%',
        width: '100%',
        maxWidth: `${CATALOGO_TARGET_CARD_MAX_WIDTH_PX}px`,
        justifySelf: 'center',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: 8,
        },
      }}
      data-card-max-width={CATALOGO_TARGET_CARD_MAX_WIDTH_PX}
    >
      <Box onClick={() => onClick(producto)} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', cursor: 'pointer' }}>
        <CatalogoImageCarousel
          imagenes={producto.imagenes}
          height={180}
          categoriaColor="#e8d8e8"
          onCurrentImageChange={setActiveImageUrl}
        />

        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ color: '#3d1e3d', lineHeight: 1.3, mb: 0.5 }}
            noWrap
            title={producto.nombre}
            data-testid="catalogo-card-nombre"
          >
            {producto.nombre}
          </Typography>

          {producto.categoria && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
              {producto.categoria.nombre}
              {producto.subcategoria && ` › ${producto.subcategoria.nombre}`}
            </Typography>
          )}

          <Box sx={{ mt: 1 }} data-testid="catalogo-card-precios">
            {producto.precioDesde !== null ? (
              <>
                <Typography variant="body2" color="text.secondary" component="span">
                  Desde{' '}
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="#3d1e3d" component="span" data-testid="catalogo-card-precio-desde">
                  {formatPrecio(producto.precioDesde)}
                </Typography>
                {producto.precioHasta !== null && producto.precioHasta !== producto.precioDesde && (
                  <>
                    <Typography variant="body2" color="text.secondary" component="span">
                      {' '}hasta{' '}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="span">
                      {formatPrecio(producto.precioHasta)}
                    </Typography>
                  </>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Precio no disponible
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography
              variant="caption"
              data-testid="catalogo-card-stock"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: stockBajo ? '#fdf0f0' : producto.stockTotal > 50 ? '#eaf5f2' : '#fdf5e0',
                color: stockBajo ? '#8a3d3d' : producto.stockTotal > 50 ? '#3d7a6e' : '#8a6e30',
                fontWeight: 600,
              }}
            >
              Stock: {producto.stockTotal}
            </Typography>
          </Box>

          {coloresDisponibles.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }} data-testid="catalogo-card-colores">
              {coloresDisponibles.map((colorVariante, idx) => (
                <Tooltip
                  key={idx}
                  title={`Stock: ${colorVariante.stock}`}
                  arrow
                  placement="top"
                >
                  <Box
                    data-testid="color-bolita"
                    data-color={resolveColor(colorVariante)}
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: resolveColor(colorVariante),
                      border: '1.5px solid rgba(0,0,0,0.15)',
                      flexShrink: 0,
                      cursor: 'default',
                    }}
                  />
                </Tooltip>
              ))}
              {producto.coloresDisponibles.length > 5 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center' }} data-testid="colores-extra">
                  +{producto.coloresDisponibles.length - 5}
                </Typography>
              )}
            </Stack>
          )}
        </CardContent>
      </Box>

      {onAgregarClick && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onAgregarClick(producto, activeImageUrl);
          }}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            backgroundColor: '#a0588e',
            color: 'white',
            width: 40,
            height: 40,
            zIndex: 10,
            '&:hover': {
              backgroundColor: '#8a4a78',
            },
          }}
          aria-label="Agregar a bolsa"
          data-testid="btn-agregar-producto"
        >
          <AddIcon fontSize="small" />
        </IconButton>
      )}
    </Card>
  );
}
